import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiTrash, HiPlus, HiChevronDown, HiChevronRight } from 'react-icons/hi';

import { Button, Table, TableColumn } from '@/components/common';
import { FormattedNumberInput, formatQuantity } from '@/utils/number-format';
import MaterialSearchInput from './MaterialSearchInput';
import CuttingConfigSearchInput from './CuttingConfigSearchInput';
import { Material } from '@/types/material';
import { CuttingConfig, CuttingFormLine, CuttingFormOutput } from '@/types/cutting';
import CuttingOrderLineTable from './CuttingOrderLineTable';


// Extended interface for cutting form line with frontend-specific fields
interface CuttingFormLineWithMeta extends CuttingFormLine {
  // Frontend-specific fields for form management
  materialId?: string; // For backward compatibility
  materialName?: string; // For backward compatibility
  materialCode?: string; // For backward compatibility
  materialUnit?: string; // For backward compatibility
  materialAvailableQuantity?: number; // Available quantity for validation (calculated)
  initialMaxAvailableQuantity?: number; // Fixed max quantity set when line is loaded/created
  cuttingConfigId?: string; // For backward compatibility
  cuttingConfigName?: string; // For backward compatibility
  cuttingType?: number; // For backward compatibility
  inputQty?: number; // For backward compatibility
  isNew?: boolean;
  isDeleted?: boolean;
  isModified?: boolean;
}

interface CuttingFormLineManagerProps {
  cuttingFormId?: string;
  initialLines?: CuttingFormLineWithMeta[];
  onChange: (lines: CuttingFormLineWithMeta[]) => void;
  disabled?: boolean;
  actualQtyEditable?: boolean; // Control actual quantity editing
  nestedTableNoteEditable?: boolean; // Control nested table note editing
  nestedTableExpandable?: boolean; // Control nested table expansion
}

export interface CuttingFormLineManagerRef {
  resetForm: () => void;
}

const CuttingFormLineManager = forwardRef<CuttingFormLineManagerRef, CuttingFormLineManagerProps>(({
  initialLines = [],
  onChange,
  disabled = false,
  actualQtyEditable = false,
  nestedTableNoteEditable = false,
  // nestedTableExpandable is not used in this component
}, ref) => {
  const { t } = useTranslation();

  // Prevent form submission on Enter key press in input fields
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };
  const [lines, setLines] = useState<CuttingFormLineWithMeta[]>(initialLines);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Two-step selection state
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [selectedCuttingConfig, setSelectedCuttingConfig] = useState<CuttingConfig | null>(null);

  const handleToggleExpand = (index: number) => {
    const rowKey = `row-${index}`;
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowKey)) {
      newExpandedRows.delete(rowKey);
    } else {
      newExpandedRows.add(rowKey);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleOutputsChange = (lineIndex: number, newOutputs: CuttingFormOutput[]) => {
    const originalLines = [...lines];
    const lineToUpdate = originalLines[lineIndex];
    if (!lineToUpdate) return;

    const updatedLine = {
      ...lineToUpdate,
      outputs: newOutputs,
      isModified: !lineToUpdate.isNew,
    };

    const newLines = [...originalLines];
    newLines[lineIndex] = updatedLine;
    setLines(newLines);
  };

  // Initialize lines from props
  useEffect(() => {
    if (initialLines.length > 0) {
      // Mark existing lines as not new and not modified, calculate and fix max available quantity
      const processedLines = initialLines.map(line => {
        const maxAvailableQty = line.material?.availableQuantity
          ? line.material.availableQuantity + (line.input_qty || line.inputQty || 0)
          : 0;

        return {
          ...line,
          isNew: false,
          isModified: false,
          isDeleted: false,
          materialAvailableQuantity: maxAvailableQty,
          initialMaxAvailableQuantity: maxAvailableQty, // Store fixed max quantity
        };
      });
      setLines(processedLines);
    } else {
      setLines([]);
    }
  }, [initialLines]);

  // Update parent when lines change
  useEffect(() => {
    onChange(lines);
  }, [lines, onChange]);

  // Get the fixed maximum available quantity for a cutting form line
  // Returns the initially calculated max quantity to prevent dynamic changes during editing
  const getFixedMaxAvailableQuantity = (line: CuttingFormLineWithMeta): number => {
    // Use the fixed initial max quantity if available, otherwise calculate it
    if (line.initialMaxAvailableQuantity !== undefined) {
      return line.initialMaxAvailableQuantity;
    }

    // Fallback calculation for lines that don't have the fixed value set
    const materialAvailableQty = line.material?.availableQuantity || 0;
    const currentLineQty = line.isNew ? 0 : (line.input_qty || line.inputQty || 0);
    return materialAvailableQty + currentLineQty;
  };




  // Handle material selection (step 1)
  const handleMaterialSelect = (material: Material | null) => {
    setSelectedMaterial(material);
    // Clear cutting config when material changes
    if (!material) {
      setSelectedCuttingConfig(null);
    }
  };

  // Handle cutting config selection (step 2)
  const handleCuttingConfigSelect = (cuttingConfig: CuttingConfig | null) => {
    setSelectedCuttingConfig(cuttingConfig);
  };

  // Add new line when both material and cutting config are selected
  const handleAddLine = () => {
    if (!selectedMaterial || !selectedCuttingConfig) {
      return;
    }

    const newLine: CuttingFormLineWithMeta = {
      id: '',
      code: '',
      cutting_form_id: '',
      raw_material_id: selectedMaterial.code,
      material_name: selectedMaterial.name,
      material: selectedMaterial, // Include complete material information
      cutting_config_id: selectedCuttingConfig.code,
      cutting_config_name: selectedCuttingConfig.name,
      cutting_type: 1, // Default to angle cutting
      input_qty: 1,
      material_category_group_id: selectedMaterial.categoryGroupId || selectedMaterial.groupId || '',
      outputs: [],
      createdAt: '',
      updatedAt: '',
      // Backward compatibility fields
      materialId: selectedMaterial.code,
      materialName: selectedMaterial.name,
      materialCode: selectedMaterial.code,
      materialUnit: selectedMaterial.unit,
      materialAvailableQuantity: selectedMaterial.availableQuantity, // For new lines, this equals material.availableQuantity
      initialMaxAvailableQuantity: selectedMaterial.availableQuantity, // Fix max quantity for new lines
      cuttingConfigId: selectedCuttingConfig.code,
      cuttingConfigName: selectedCuttingConfig.name,
      cuttingType: 1,
      inputQty: 1,
      isNew: true,
    };

    setLines(prev => [...prev, newLine]);

    // Clear selections for next addition
    setSelectedMaterial(null);
    setSelectedCuttingConfig(null);
  };

  // Remove line
  const handleRemoveLine = (index: number) => {
    setLines(prev => {
      const newLines = [...prev];
      const line = newLines[index];
      
      if (line.isNew) {
        // Remove new lines completely
        newLines.splice(index, 1);
      } else {
        // Mark existing lines as deleted
        newLines[index] = { ...line, isDeleted: true };
      }
      
      return newLines;
    });

    // Clear related errors
    const errorKey = `line_${index}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setLines([]);
    setErrors({});
    setSelectedMaterial(null);
    setSelectedCuttingConfig(null);
    onChange([]);
  };

  // Expose resetForm function to parent component
  useImperativeHandle(ref, () => ({
    resetForm,
  }));

  // Update line field
  const handleLineChange = (
    index: number,
    field: keyof CuttingFormLineWithMeta,
    value: any
  ) => {
    setLines(prev => {
      const newLines = [...prev];
      const line = newLines[index];

      // Special handling for cutting_type to sync both fields
      if (field === 'cutting_type') {
        newLines[index] = {
          ...line,
          cutting_type: value,
          cuttingType: value, // Sync backward compatibility field
          isModified: !line.isNew, // Mark as modified if not new
        };
      }
      // Special handling for input_qty to sync both fields and validate against fixed maximum
      else if (field === 'input_qty') {
        // Use the fixed maximum available quantity to prevent dynamic changes
        const maxAvailableQty = getFixedMaxAvailableQuantity(line);

        // Validate quantity against fixed maximum
        let validatedValue = value;
        if (maxAvailableQty > 0 && value > maxAvailableQty) {
          validatedValue = maxAvailableQty;
        }

        newLines[index] = {
          ...line,
          input_qty: validatedValue,
          inputQty: validatedValue, // Sync backward compatibility field
          // Keep materialAvailableQuantity and initialMaxAvailableQuantity unchanged
          isModified: !line.isNew, // Mark as modified if not new
        };
      } else {
        newLines[index] = {
          ...line,
          [field]: value,
          isModified: !line.isNew, // Mark as modified if not new
        };
      }

      return newLines;
    });

    // Clear related errors
    const errorKey = `line_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };



  // Filter out deleted lines for display
  const visibleLines = lines.filter(line => !line.isDeleted);

  // Table columns
  const columns: TableColumn<CuttingFormLineWithMeta>[] = [
    {
      key: 'expand',
      title: '',
      width: 50,
      render: (_, __, index) => (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => handleToggleExpand(index)}
          icon={expandedRows.has(`row-${index}`) ? <HiChevronDown /> : <HiChevronRight />}
          disabled={false}
        />
      ),
    },
    {
      key: 'stt',
      title: t('cuttingOrders.form.tableHeaders.stt'),
      width: 60,
      align: 'center',
      render: (_, __, index) => (
        <div className="text-sm font-medium text-gray-900">
          {index + 1}
        </div>
      ),
    },
    {
      key: 'materialName',
      title: t('cuttingOrders.form.tableHeaders.materialName'),
      width: 200,
      render: (_, record) => (
        <div className="space-y-1">
          <div className="font-medium text-gray-900 truncate">
            {record.material_name || record.materialName || 'Unknown Material'}
          </div>
          <div className="text-sm text-gray-500 truncate">
            {record.raw_material_id || record.materialId || 'N/A'}
          </div>
        </div>
      ),
    },

    {
      key: 'inputQty',
      title: t('cuttingOrders.form.tableHeaders.inputQty'),
      width: 150,
      render: (_, record) => {
        const actualIndex = lines.findIndex(line => line === record);
        const currentValue = record.input_qty || record.inputQty || 0;
        const maxAvailableQty = getFixedMaxAvailableQuantity(record);
        const materialUnit = record.material?.unit || record.materialUnit || '';
        return (
          <div className="space-y-1">
            <FormattedNumberInput
              value={currentValue}
              onChange={(value) => handleLineChange(actualIndex, 'input_qty', Math.max(1, Math.min(maxAvailableQty, value)))}
              className={`input-field ${errors[`line_${actualIndex}_input_qty`] ? 'border-red-500' : ''}`}
              disabled={disabled}
              onKeyDown={handleKeyDown}
            />
            <div className="text-xs text-gray-500">
              Max: {formatQuantity(maxAvailableQty)} {materialUnit}
              {!record.isNew && (
                <span className="text-blue-600 ml-1">
                  (Available: {formatQuantity(record.material?.availableQuantity || 0)} + Initial: {formatQuantity(record.input_qty || record.inputQty || 0)})
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'cuttingConfigId',
      title: t('cuttingOrders.form.tableHeaders.cuttingConfig'),
      width: 180,
      render: (_, record) => (
        <div className="space-y-1">
          <div className="font-medium text-gray-900 truncate">
            {record.cutting_config_name || record.cuttingConfigName || 'Unknown Config'}
          </div>
          <div className="text-sm text-gray-500 truncate">
            {record.cutting_config_id || record.cuttingConfigId || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      key: 'cuttingType',
      title: t('cuttingOrders.form.tableHeaders.cuttingType'),
      width: 120,
      render: (_, record) => {
        const actualIndex = lines.findIndex(line => line === record);
        const currentValue = record.cutting_type || record.cuttingType || 1;
        return (
          <select
            value={currentValue}
            onChange={(e) => handleLineChange(actualIndex, 'cutting_type', Number(e.target.value))}
            className={`input-field ${errors[`line_${actualIndex}_cutting_type`] ? 'border-red-500' : ''}`}
            disabled={disabled}
          >
            <option value={1}>{t('cuttingOrders.form.cuttingTypes.angle')}</option>
            <option value={2}>{t('cuttingOrders.form.cuttingTypes.square')}</option>
          </select>
        );
      },
    },
    {
      key: 'actions',
      title: t('cuttingOrders.form.tableHeaders.actions'),
      width: 50,
      align: 'center',
      render: (_, record) => {
        const actualIndex = lines.findIndex(line => line === record);
        return (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleRemoveLine(actualIndex)}
            icon={<HiTrash className="h-4 w-4" />}
            className="text-red-600 hover:text-red-700 hover:border-red-300"
            title={t('cuttingOrders.form.deleteRow')}
            disabled={disabled}
          />
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('cuttingOrders.form.title')}
        </h3>

        {/* Two-step selection process */}
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('cuttingOrders.form.addNewMaterial')}</h4>

          <div className="flex gap-4 items-end">
            {/* Step 1: Material Selection */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('cuttingOrders.form.step1')}
              </label>
              <MaterialSearchInput
                value={selectedMaterial}
                onChange={handleMaterialSelect}
                placeholder={t('cuttingOrders.form.searchMaterial')}
                disabled={disabled}
                className="w-full"
                enforceAvailability={true}
              />
            </div>

            {/* Step 2: Cutting Config Selection */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('cuttingOrders.form.step2')}
              </label>
              <CuttingConfigSearchInput
                value={selectedCuttingConfig}
                onChange={handleCuttingConfigSelect}
                placeholder={t('cuttingOrders.form.searchCuttingConfig')}
                disabled={disabled || !selectedMaterial}
                className="w-full"
              />
            </div>

            {/* Add Button */}
            <div className="flex-shrink-0">
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={handleAddLine}
                disabled={disabled || !selectedMaterial || !selectedCuttingConfig}
                icon={<HiPlus className="h-4 w-4" />}
              >
                {t('cuttingOrders.form.addToTable')}
              </Button>
            </div>
          </div>

          {/* Selection Status */}
          <div className="mt-3 text-sm text-gray-600">
            {selectedMaterial && selectedCuttingConfig ? (
              <span className="text-green-600">{t('cuttingOrders.form.statusReady')}</span>
            ) : selectedMaterial ? (
              <span className="text-yellow-600">{t('cuttingOrders.form.statusNeedConfig')}</span>
            ) : (
              <span className="text-gray-500">{t('cuttingOrders.form.statusNeedBoth')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-x-auto">
        <div className="min-w-full">
          <Table
            columns={columns}
            data={visibleLines}
            rowKey={(record: CuttingFormLineWithMeta) => `line-${record.raw_material_id || record.materialId || 'new'}-${record.cutting_config_id || record.cuttingConfigId || 'no-config'}`}
            size="sm"
            emptyText={t('cuttingOrders.form.emptyTable')}
            renderExpandedRow={(record, index) => {
              const rowKey = `row-${index}`;
              if (!expandedRows.has(rowKey) || !record.outputs) {
                return null;
              }
              return (
                <td colSpan={columns.length}>
                  <div className="overflow-x-auto">
                    <CuttingOrderLineTable
                      outputs={record.outputs}
                      onOutputsChange={(newOutputs) => handleOutputsChange(index, newOutputs)}
                      actualQtyEditable={actualQtyEditable}
                      noteEditable={nestedTableNoteEditable}
                    />
                  </div>
                </td>
              );
            }}
          />
        </div>
      </div>

      {/* Summary */}
      {visibleLines.length > 0 && (
        <div className="text-sm text-gray-600">
          {t('cuttingOrders.form.summary', { count: visibleLines.length })}
        </div>
      )}
    </div>
  );
});

export default CuttingFormLineManager;
